import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
// eslint-disable-next-line no-unused-vars
import { Box, Chip, IconButton, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNavigate, useParams } from 'react-router';
// eslint-disable-next-line no-unused-vars
import { MAX_VISIBLE } from '../utils/transcriptUtils';

const useStyles = makeStyles((theme) => ({
  box: {
    width: '100%',
    height: '100%',
    minHeight: '46px',
    borderRadius: '6px',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    border: '1px solid #eee',
    paddingRight: '10px',
    paddingLeft: '10px',
    marginRight: '16px',
  },
  listItemIconBox: {
    width: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    padding: '8px',
    marginRight: '8px',
  },
  listItem: {
    padding: '0 !important',
    marginBottom: '6px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  listItemActive: {
    border: '2px solid #7D5AE8',
  },
  filterCountBadge: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50% !important',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: '0.75rem',
    border: '2px solid white',
    zIndex: 99,
  },
}));

const TranscriptItem = ({
  transcript,
  index,
  isActive,
  getTranscriptName,
  assignedTags = [],
  onMenuClick,
  flatBottom = false,
  derivedCount = 0,
  derivedExpanded = false,
  onToggleDerived,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();

  const transcriptIndex = transcript?.transcript_index;
  const fallbackName = transcriptIndex
    ? `Transcript ${transcriptIndex}`
    : `Transcript ${getTranscriptName(transcript?.id)}`;
  const rawName =
    transcript?.name && transcript?.name !== 'Interview started' ? transcript?.name : fallbackName;

  const isDerived = transcript?.is_derived === true;
  // For derived transcripts, show base name + workflow label as the display name
  const displayName = (() => {
    if (!isDerived) return rawName;
    const baseName = rawName.includes('•') ? rawName.split('•')[0].trim() : rawName;
    const wf = transcript?.derived_workflow_type;
    if (wf === 'translate' && transcript?.target_language_code) {
      return `${baseName} • Translated (${transcript.target_language_code})`;
    }
    if (wf === 'pii_redaction') return `${baseName} • PII Redacted`;
    if (wf === 'enhance_transcription') return `${baseName} • Enhanced`;
    return baseName;
  })();

  const status = (transcript?.interview_status || '').toLowerCase();
  const statusMeta = (() => {
    if (status === 'active') {
      return {
        label: 'Active',
        color: 'info',
        tooltip:
          'This session might still be active. If this session was discarded, it will auto-resolve to complete or incomplete within an hour.',
      };
    }
    if (status === 'draft') {
      return {
        label: 'Draft',
        color: 'warning',
        tooltip:
          'This session was not marked as complete by the AI interviewer. You can manually mark it as complete using the ... options if you want this to be part of any analysis.',
      };
    }
    if (status === 'incomplete') {
      return {
        label: 'Incomplete',
        color: 'warning',
        tooltip:
          'This session was not marked as complete by the AI interviewer. You can manually mark it as complete using the ... options if you want this to be part of any analysis.',
      };
    }
    if (status === 'closed') {
      return {
        label: 'Complete',
        color: 'success',
        tooltip: '',
      };
    }
    if (transcript?.is_complete === false) {
      return {
        label: 'Incomplete',
        color: 'warning',
        tooltip:
          'This session was not marked as complete by the AI interviewer. You can manually mark it as complete using the ... options if you want this to be part of any analysis.',
      };
    }
    return { label: 'Complete', color: 'success', tooltip: '' };
  })();

  const handleTranscriptClick = () => {
    navigate(`/interview/${id}/transcripts/${transcript.id}`, {
      replace: true,
      state: {
        transcriptName: displayName,
      },
    });
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    // Create transcript object with name for delete confirmation
    const transcriptWithName = {
      ...transcript,
      name: displayName,
    };
    onMenuClick && onMenuClick(event, transcriptWithName);
  };

  const isSecondary = transcript?.is_primary === false;
  // eslint-disable-next-line no-unused-vars
  const workflowLabel = (() => {
    if (!isDerived) return null;
    if (transcript?.derived_workflow_type === 'translate' && transcript?.target_language_code) {
      return `Translated (${transcript.target_language_code})`;
    }
    if (transcript?.derived_workflow_type === 'pii_redaction') {
      return 'PII Redacted';
    }
    if (transcript?.derived_workflow_type === 'enhance_transcription') {
      return 'Enhanced';
    }
    return 'Derived';
  })();

  return (
    <>
      <ListItem
        className={`${classes.listItem}`}
        onClick={handleTranscriptClick}
        sx={flatBottom ? { mb: '0 !important' } : {}}
      >
        <Box className={classes.listItemIconBox}>
          {!isDerived && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {index + 1}.
            </Typography>
          )}
        </Box>
        <Box
          className={`${classes.box}  ${isActive ? classes.listItemActive : ''}`}
          sx={
            flatBottom
              ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }
              : {}
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              minHeight: 32,
              py: 0.25,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
              }}
            >
              <Tooltip title={`${isDerived ? rawName : displayName} — ${statusMeta.label}`} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: isDerived ? '0.68rem' : '0.75rem',
                    fontWeight: isDerived ? 500 : 600,
                    flexShrink: 0,
                    minWidth: '60px',
                    maxWidth: '55%',
                  }}
                >
                  {displayName}
                </Typography>
              </Tooltip>
              {!isDerived && (
                <Tooltip title={statusMeta.tooltip || statusMeta.label} arrow>
                  <Chip
                    label={statusMeta.label}
                    color={statusMeta.color}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      '& .MuiChip-label': { px: 0.5 },
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
              {!isDerived && isSecondary && (
                <Chip
                  label="Secondary"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    '& .MuiChip-label': { px: 0.5 },
                    flexShrink: 0,
                    borderColor: '#9e9e9e',
                    color: '#666',
                  }}
                />
              )}
              {/* Documents get an explicit "Document" chip; transcripts have no chip
                  (the absence of this chip means "transcript", consistent with how
                  the Type column in TranscriptWorkflowModal styles documents). */}
              {!isDerived && transcript?.item_type === 'document' && (
                <Chip
                  label="Document"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    '& .MuiChip-label': { px: 0.5 },
                    flexShrink: 0,
                    bgcolor: '#f3e8ff',
                    color: '#6b21a8',
                    borderColor: '#e9d5ff',
                  }}
                />
              )}
              {assignedTags?.length > 0 && (
                <Tooltip
                  title={assignedTags
                    .map(
                      (tag) =>
                        `${tag?.tag_key}: ${tag?.values?.map((v) => v.value_key).join(', ')}`,
                    )
                    .join(' | ')}
                  arrow
                >
                  <Chip
                    label={`${assignedTags.length} tag${assignedTags.length > 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      '& .MuiChip-label': { px: 0.5 },
                      flexShrink: 0,
                      bgcolor: '#f0f0f0',
                      color: '#666',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <IconButton
              size="small"
              aria-label="More options"
              data-transcript-id={transcript.id}
              onClick={handleMenuClick}
              sx={{ flexShrink: 0, ml: 0.25 }}
            >
              <MoreHorizIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </ListItem>
      {derivedCount > 0 && onToggleDerived && (
        <Box
          sx={{ display: 'flex', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleDerived();
          }}
        >
          <Box className={classes.listItemIconBox} sx={{ visibility: 'hidden' }} />
          <Box
            sx={{
              flex: 1,
              mr: '16px',
              px: 1.5,
              py: 0.5,
              bgcolor: 'white',
              border: '1px solid #eee',
              borderTop: 'none',
              borderLeft: '3px solid #C9B7FF',
              borderBottomLeftRadius: derivedExpanded ? 0 : '6px',
              borderBottomRightRadius: derivedExpanded ? 0 : '6px',
              mb: derivedExpanded ? 0 : 1,
              transition: 'all 0.15s ease',
              '&:hover': { bgcolor: '#f8f7ff' },
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: '0.7rem', color: '#5e35b1', fontWeight: 600 }}
            >
              {derivedExpanded
                ? `\u25B4  Hide ${derivedCount} derived`
                : `\u25BE  ${derivedCount} derived version${derivedCount > 1 ? 's' : ''}`}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default TranscriptItem;
